import { useEffect, useState } from 'react';
import { fetchMembershipsActingAs } from '@/lib/roles/membershipsActingAs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Users, Clock } from 'lucide-react';
import { getDay } from 'date-fns';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { toZonedTime } from 'date-fns-tz';
// DTC-G G3: route the staff-presence today-day-of-week anchor through
// the canonical clock so the live presence panel is deterministic
// under `clock.setFixed(...)`. The `toZonedTime(...)` derivation
// (class-C already-zoned) is preserved.
import { clock } from '@/utils/clock';
import { fetchPersonDisplayNames, UNKNOWN_MEMBER_LABEL } from '@/lib/people/personDisplayName';

interface StaffSchedule {
  user_id: string;
  role: 'MANAGER' | 'SERVICE_WRITER';
  shift_start: string;
  shift_end: string;
  working_days: number[];
}

interface StaffMember {
  user_id: string;
  display_name: string;
  role: 'MANAGER' | 'SERVICE_WRITER';
  is_clocked_in: boolean;
  schedule: StaffSchedule;
}

interface StaffPresenceIndicatorProps {
  refreshKey?: number;
}

export const StaffPresenceIndicator = ({ refreshKey = 0 }: StaffPresenceIndicatorProps) => {
  const { activeShop } = useAuth();
  const { shopTimezone } = useShopTimezone();
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!activeShop?.id) return;
    loadStaff();
  }, [activeShop?.id, refreshKey]);

  const loadStaff = async () => {
    try {
      // Get staff schedules
      const { data: profile } = await supabase
        .from('shop_profile')
        .select('staff_schedules')
        .eq('shop_id', activeShop!.id)
        .maybeSingle();

      const staffSchedules = (profile?.staff_schedules || []) as unknown as StaffSchedule[];

      // Get all managers and service writers
      // Multi-role: effective roles, so a Technician + Service Advisor is counted as front office.
      const memberships = await fetchMembershipsActingAs(supabase, activeShop!.id, ['MANAGER', 'SERVICE_WRITER']);

      if (!memberships) {
        setStaff([]);
        return;
      }

      // Check who's clocked in
      const userIds = memberships.map(m => m.user_id);
      const { data: clockedIn } = await supabase
        .from('time_clock_entries')
        .select('user_id')
        .in('user_id', userIds)
        .is('clock_out_time', null);

      const clockedInUserIds = new Set(clockedIn?.map(c => c.user_id) || []);

      // Names through the canonical person-name rule (the profiles embed
      // resolved through memberships.removed_by and was always null).
      const names = await fetchPersonDisplayNames(supabase, memberships.map((m) => m.user_id), activeShop!.id);

      // Filter staff who are scheduled today
      const today = getDay(toZonedTime(clock.now(), shopTimezone));
      const staffMembers: StaffMember[] = memberships
        .map(m => {
          const schedule = staffSchedules.find(s => s.user_id === m.user_id);
          const isScheduledToday = schedule?.working_days.includes(today) ?? false;

          // Only show if they have a schedule and are scheduled today
          if (!schedule || !isScheduledToday) return null;
          if (m.role !== 'MANAGER' && m.role !== 'SERVICE_WRITER') return null;

          return {
            user_id: m.user_id,
            display_name: names[m.user_id] ?? UNKNOWN_MEMBER_LABEL,
            role: m.role as 'MANAGER' | 'SERVICE_WRITER',
            is_clocked_in: clockedInUserIds.has(m.user_id),
            schedule
          };
        })
        .filter((s): s is StaffMember => s !== null);

      setStaff(staffMembers);
    } catch (error) {
      console.error('Error loading staff presence:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || staff.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Users className="h-4 w-4" />
          Staff Present
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {staff.map(member => (
          <div key={member.user_id} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Clock className="h-3 w-3 text-muted-foreground" />
              <span>{member.display_name}</span>
              <span className="text-xs text-muted-foreground">
                ({member.role.replace('_', ' ')})
              </span>
            </div>
            <Badge variant={member.is_clocked_in ? 'default' : 'secondary'} className="text-xs">
              {member.is_clocked_in ? 'Present' : 'Away'}
            </Badge>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
